import * as React from 'react';
import * as _ from 'lodash';
import { Redirect } from 'react-router-dom';
import {
  ContentLoading,
  Logging,
  Notification,
  ResponseError,
  API,
  NotificationData,
  EmailConfiguration,
  getUuidV4,
} from '@vcd-ecm-ui/Common';
import { useDispatch } from 'react-redux';
import { VtxSteps, VtxStep } from '@vertexinc/vtx-ui-react-component-library';
import Step1 from './Step1/Step1';
import Step2 from './Step2/Step2';
import Step3 from './Step3/Step3';
import ConfigurationStepsProps from './ConfigurationStepsProps.interface';
import useConfiguration, { DefaultDomainName } from '../useConfiguration';
import '../../common-correspondence.scss';
import '../configuration.scss';

interface Step {
  key: number;
  title: string;
  description?: string;
  content: JSX.Element;
}

const ConfigurationSteps = ({
  gatewayEndPoint,
}: ConfigurationStepsProps): JSX.Element => {
  const numberSteps = 3;
  const [redirect, setRedirect] = React.useState(null);
  const [selectedStep, setSelectedStep] = React.useState(0);
  const [notification, setNotification] = React.useState(null);
  const [newConfiguration, setNewConfiguration] = React.useState(null); // Current Configuration
  const [reloadKey, setReloadKey] = React.useState(getUuidV4());

  const [configuration, configurationNotification] = useConfiguration(gatewayEndPoint, reloadKey);

  const dispatch = useDispatch();

  // Make the API Call to Save the Configuration
  const saveConfiguration = (configurationUpdated: EmailConfiguration) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      Logging.Info('Saving Email Configuration', configurationUpdated);

      // Save the Email Configuration
      await api.correspondence.setEmailConfigurationV41(configurationUpdated);

      // Direct to Configuration Status
      setRedirect('/correspondence/configuration/status');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Configuration Save Error',
        description: 'Unable to Save Configuration',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  const onNext = (updatedConfiguration: EmailConfiguration): void => {
    Logging.Info('Next', selectedStep);

    setNewConfiguration(updatedConfiguration);

    // More Steps to Come
    if (selectedStep < (numberSteps - 1)) {
      setSelectedStep(selectedStep + 1);
    } else { // final Step
      // Save the Configuration
      dispatch(saveConfiguration(updatedConfiguration));
    }
  };

  const onBack = (): void => {
    Logging.Info('Back', selectedStep);

    if (selectedStep > 0) {
      setSelectedStep(selectedStep - 1);
    }
  };

  const onCancel = (): void => {
    Logging.Info('Cancel', selectedStep);

    // Direct to Configuration Status
    setRedirect('/correspondence/configuration/status');
  };

  const steps: Step[] = [
    {
      key: 1,
      title: 'Domain Authentication',
      description: 'Configure the Domain Name',
      content: <Step1 gatewayEndPoint={gatewayEndPoint} configuration={newConfiguration} onBack={onBack} onNext={onNext} onCancel={onCancel} />,
    },
    {
      key: 2,
      title: 'Address Service',
      description: 'Configure the Email Address Service',
      content: <Step2 gatewayEndPoint={gatewayEndPoint} configuration={newConfiguration} onBack={onBack} onNext={onNext} onCancel={onCancel} />,
    },
    {
      key: 3,
      title: 'DNS Records',
      description: 'Configure the DNS Records',
      content: <Step3 gatewayEndPoint={gatewayEndPoint} configuration={newConfiguration} onBack={onBack} onNext={onNext} onCancel={onCancel} />,
    },
  ];

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Step Selected
  const onSelectStep = (step: number): void => {
    // Do not Change the Steps here as it will bypass the validation
  };

  // Configuration was Loaded
  React.useEffect(() => {
    if (configuration) {
      const vertexDomain: boolean = configuration?.domain?.toLowerCase() === DefaultDomainName.toLowerCase();

      // Use Blank Values if Currently the Vertex Domain
      setNewConfiguration(vertexDomain ? { domain: null, fromEmailAddress: null, errorEmailAddress: null } : configuration);
    }
  }, [configuration]);

  React.useEffect(() => {
    setNotification(configurationNotification);
  }, [configurationNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Configuration: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setNotification(null);
      setRedirect(null);
      Logging.Info('Configuration Unmounting');
    };
  }, []);

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  return (
    <>
      <div className="correspondenceContainer">
        <div className="correspondenceHeader">
          Configuration
        </div>
        {
          newConfiguration
            && (
              <div className="correspondenceContent stepsContainer">
                <VtxSteps current={selectedStep} onChange={onSelectStep} size="default">
                  {steps.map((step: Step, index: number) => (
                    <VtxStep key={step.key} title={step.title} />))}
                </VtxSteps>
                <hr />
                <div className="stepContainer">
                  {steps[selectedStep].content}
                </div>
              </div>
            )
        }
        {
          !newConfiguration && !configurationNotification
            && (
              <div className="correspondenceContent">
                <ContentLoading loadingMessage="Loading Configuration ..." />
              </div>
            )
        }
      </div>
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default ConfigurationSteps;
